import { useState, useMemo } from 'react';
import type { ValidatorInfo } from '@/types/staking';
import {
  formatTokenAmount,
  formatCommission,
  formatValidatorStatus,
  getValidatorStatusColor,
  shortenAddress,
} from '@/utils/formatting';
import { getExcludedTopValidatorAddresses } from '@/utils/redelegation';
import { Loader, Table, type Column, Button, CopyableAddress } from '@/components/common';

interface ValidatorListProps {
  validators: ValidatorInfo[];
  isLoading: boolean;
  onDelegate: (validator: ValidatorInfo) => void;
}

export function ValidatorList({ validators, isLoading, onDelegate }: ValidatorListProps): JSX.Element {
  const [sortBy, setSortBy] = useState<'voting_power' | 'commission' | 'moniker'>('voting_power');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc');
  const [showInactive, setShowInactive] = useState(false);

  // Compute the excluded top-validator set once from the full unfiltered list
  const excludedSet = useMemo(() => getExcludedTopValidatorAddresses(validators), [validators]);

  if (isLoading) {
    return (
      <div className="flex justify-center items-center py-12">
        <Loader size="lg" />
      </div>
    );
  }

  // Filter validators based on showInactive toggle
  const filteredValidators = showInactive 
    ? validators 
    : validators.filter(v => v.status === 3 && !v.jailed);

  const sortedValidators = [...filteredValidators].sort((a, b) => {
    let aVal: string | number;
    let bVal: string | number;

    switch (sortBy) {
      case 'voting_power':
        aVal = parseFloat(a.tokens);
        bVal = parseFloat(b.tokens);
        break;
      case 'commission':
        aVal = parseFloat(a.commission ?? '0');
        bVal = parseFloat(b.commission ?? '0');
        break;
      case 'moniker':
        aVal = a.description?.moniker ?? '';
        bVal = b.description?.moniker ?? '';
        break;
      default:
        aVal = 0;
        bVal = 0;
    }

    if (typeof aVal === 'string' && typeof bVal === 'string') {
      return sortOrder === 'asc'
        ? aVal.localeCompare(bVal)
        : bVal.localeCompare(aVal);
    }

    return sortOrder === 'asc' ? (aVal as number) - (bVal as number) : (bVal as number) - (aVal as number);
  });

  const columns: Column<ValidatorInfo>[] = [
    { 
      key: 'moniker', 
      header: 'Validator',
      render: (validator) => (
        <div>
          <div className="font-medium">
            {validator.description?.moniker ?? shortenAddress(validator.operator_address)}
          </div>
          <CopyableAddress address={validator.operator_address} />
        </div>
      )
    },
    { 
      key: 'voting_power', 
      header: 'Voting Power',
      render: (validator) => (
        <div>
          <div className="font-medium">{formatTokenAmount(validator.tokens)} GRAV</div>
        </div>
      )
    },
    { 
      key: 'commission', 
      header: 'Commission',
      render: (validator) => formatCommission(validator.commission)
    },
    { 
      key: 'status', 
      header: 'Status',
      render: (validator) => (
        <span
          className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getValidatorStatusColor(
            validator.status
          )}`}
        >
          {formatValidatorStatus(validator.status)}
        </span>
      )
    },
    { 
      key: 'actions', 
      header: '',
      render: (validator) => (
        <Button
          variant="primary"
          size="sm"
          onClick={() => onDelegate(validator)}
          disabled={validator.jailed || validator.status !== 3 || excludedSet.has(validator.operator_address)}
        >
          Delegate
        </Button>
      )
    },
  ];

  return (
    <div>
      <div className="mb-4 flex justify-between items-center">
        <div className="flex gap-4 items-center">
          <div className="text-sm text-gray-600">
            {filteredValidators.length} {showInactive ? 'total' : 'active'} validators
          </div>
          <label className="flex items-center gap-2 text-sm text-gray-700 cursor-pointer">
            <input
              type="checkbox"
              checked={showInactive}
              onChange={(e) => setShowInactive(e.target.checked)}
              className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
            />
            Show inactive validators
          </label>
        </div>
        <div className="flex gap-2">
          <select
            value={sortBy}
            onChange={(e) => setSortBy(e.target.value as typeof sortBy)}
            className="px-3 py-1 border border-gray-300 rounded-md text-sm bg-white text-gray-900"
            aria-label="Sort by"
          >
            <option value="voting_power">Voting Power</option>
            <option value="commission">Commission</option>
            <option value="moniker">Name</option>
          </select>
          <button
            onClick={() => setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc')}
            className="px-3 py-1 border border-gray-300 rounded-md text-sm bg-white hover:bg-gray-50 font-bold text-gray-700"
            aria-label={`Sort ${sortOrder === 'asc' ? 'descending' : 'ascending'}`}
          >
            {sortOrder === 'asc' ? '↑' : '↓'}
          </button>
        </div>
      </div>

      <Table columns={columns} data={sortedValidators} keyExtractor={(v) => v.operator_address} />
    </div>
  );
}
